import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import Button from "@/components/foundation/Button";
import { submitContact } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REASON_OPTIONS = [
  "General Inquiry",
  "Business Funding",
  "Funding Partner Inquiry",
  "Press",
  "Support",
];

/**
 * ContactForm — main /contact form. Visual-only.
 * Phase 4 will wire to POST /api/contact.
 */
export const ContactForm = ({
  onSubmit,
  source = "contact_page",
  className,
  "data-testid": testId = "contact-form",
}) => {
  const [values, setValues] = useState({
    name: "",
    email: "",
    company: "",
    reason: "General Inquiry",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = "Name is required.";
    if (!values.email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(values.email.trim())) next.email = "Enter a valid email.";
    if (!values.message.trim()) next.message = "Message is required.";
    else if (values.message.trim().length < 10)
      next.message = "Please add a bit more detail (min 10 characters).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;
    if (!validate()) return;

    setStatus("submitting");
    const payload = {
      name: values.name.trim(),
      email: values.email.trim(),
      company: values.company.trim() || null,
      reason: values.reason,
      message: values.message.trim(),
      source,
    };
    try {
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await submitContact(payload);
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setErrors({ _root: err?.message || "Something went wrong. Please try again in a moment." });
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "flex flex-col items-start gap-4 rounded-2xl border border-brand-emerald/25 bg-brand-emerald/8 p-8",
          className,
        )}
        data-testid="form-success"
        role="status"
        aria-live="polite"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-emerald text-black">
          <Check className="h-6 w-6" strokeWidth={3} />
        </span>
        <div>
          <h3 className="font-display text-xl font-semibold text-white">
            Thanks.
          </h3>
          <p className="mt-1 text-sm text-text-secondary">
            We&rsquo;ll get back to you within 1&ndash;2 business days.
          </p>
        </div>
      </motion.div>
    );
  }

  const control = (hasError) =>
    cn(
      "w-full rounded-xl border bg-white/[0.02] px-4 py-3 text-sm text-white placeholder:text-text-muted outline-none transition-colors duration-200",
      "focus:border-brand-emerald/60 focus:bg-white/[0.04]",
      hasError
        ? "border-red-500/60"
        : "border-white/[0.08] hover:border-white/[0.16]",
    );

  const labelCls =
    "mb-1.5 block text-[11px] font-medium uppercase tracking-[0.12em] text-text-muted";

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={cn("flex w-full flex-col gap-5", className)}
      data-testid={testId}
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <label className="flex flex-col">
          <span className={labelCls}>
            Name <span className="text-brand-emerald">*</span>
          </span>
          <input
            type="text"
            value={values.name}
            onChange={set("name")}
            aria-invalid={Boolean(errors.name)}
            className={control(Boolean(errors.name))}
            data-testid="cf-name"
          />
          {errors.name && (
            <span className="mt-1.5 pl-1 text-xs text-red-400">
              {errors.name}
            </span>
          )}
        </label>
        <label className="flex flex-col">
          <span className={labelCls}>
            Email <span className="text-brand-emerald">*</span>
          </span>
          <input
            type="email"
            value={values.email}
            onChange={set("email")}
            aria-invalid={Boolean(errors.email)}
            className={control(Boolean(errors.email))}
            data-testid="cf-email"
          />
          {errors.email && (
            <span className="mt-1.5 pl-1 text-xs text-red-400">
              {errors.email}
            </span>
          )}
        </label>
        <label className="flex flex-col">
          <span className={labelCls}>Company</span>
          <input
            type="text"
            value={values.company}
            onChange={set("company")}
            className={control(false)}
            data-testid="cf-company"
          />
        </label>
        <label className="flex flex-col">
          <span className={labelCls}>Reason</span>
          <select
            value={values.reason}
            onChange={set("reason")}
            className={cn(control(false), "appearance-none pr-8")}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7'%3E%3Cpath fill='none' stroke='%2371717A' stroke-width='1.5' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 1rem center",
            }}
            data-testid="cf-reason"
          >
            {REASON_OPTIONS.map((r) => (
              <option key={r} value={r} className="bg-bg-raised">
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex flex-col">
        <span className={labelCls}>
          Message <span className="text-brand-emerald">*</span>
        </span>
        <textarea
          value={values.message}
          onChange={set("message")}
          rows={6}
          aria-invalid={Boolean(errors.message)}
          className={cn(control(Boolean(errors.message)), "resize-none")}
          data-testid="cf-message"
        />
        {errors.message && (
          <span className="mt-1.5 pl-1 text-xs text-red-400">
            {errors.message}
          </span>
        )}
      </label>

      {errors._root && (
        <p className="text-xs text-red-400" role="alert">
          {errors._root}
        </p>
      )}

      <div className="mt-2 flex items-center justify-between gap-4">
        <p className="text-xs text-text-muted">
          We&rsquo;ll never share your info.
        </p>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={status === "submitting"}
          rightIcon={
            status === "submitting" ? null : <ArrowRight className="h-4 w-4" />
          }
          data-testid="cf-submit-btn"
        >
          {status === "submitting" ? "Sending…" : "Send Message"}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
